import React, { forwardRef, useCallback, useContext, useEffect, useState } from 'react';
import useTranslate from '../../../../../../common/hooks/useTranslate';
import { process } from '@progress/kendo-data-query';
import { useParams } from 'react-router-dom';
import FilterCtx from '../../../../../../context/filterContext/filterCtx';

import { SinglePeriodGridProps } from './singlePeriodGridProps';
import { IGridPreparedData } from '../types/IGridPreparedData';
import { ICurrency } from '../../../../types/ICurrency';

import LinkCell from '../../../../../ui/table/cells/linkCell/linkCell';
import NumericCell from '../../../cells/numericCell/numericCell';
import ColumnMenu from '../../../columnMenu/columnMenu';
import TextCell from '../../../../../ui/table/cells/textCell/textCell';
import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import CashflowCell from '../../cashflow/cells/cashflowCell/cashflowCell';
import GridFooterCell from '../cells/gridFooterCell/gridFooterCell';
import StaticPropertyCell from '../cells/staticPropertyCell/staticPropertyCell';
import MobileDataItem from '../cells/mobileDataItem/mobileDataItem';
import { getFieldName } from '../../../../../../common/utility/getFieldName';

const SinglePeriodGrid: React.FC<SinglePeriodGridProps> = forwardRef(
  ({
     gridData,
     filter,
     widget,
     gridColumns,
     selectedCurrency,
     openCashflowDialog,
     gridClassName,
     boardData,
     boards,
     isMobile,
     setup,
     sortDescriptors,
   }, ref: any) => {
    const filterCtx = useContext(FilterCtx);
    const params = useParams();

    const initialDataState = {
      ...filterCtx.gridWidgetFilterState,
      take: undefined,
      sort: [
        {
          field: 'name',
          dir: 'asc',
        },
      ],
      filter: filter,
      skip: 0,
    };

    const [dataState, setDataState] = useState<any>(filterCtx.gridWidgetFilterState);
    const [resultState, setResultState] = useState<any>({});
    const [multipleCurrencies, setMultipleCurrencies] = useState(false);

    const checkIFThereIsTotal = (columns) => {
      let total = false;
      columns.forEach((column) => {
        if (column.total) {
          total = true;
        }
      });
      return total;
    };

    const { t } = useTranslate();

    const onDataStateChange = useCallback(
      (e: GridDataStateChangeEvent) => {
        if (e.dataState.sort) e.dataState.sort = sortDescriptors(e.dataState.sort);

        setDataState(e.dataState);
        setResultState(process(gridData, e.dataState));
      },
      [gridData],
    );

    const LinkCellRender = (props) => {
      return widget.linkContentId && boards.find((item) => item.id === widget.linkContentId) ? (
        <LinkCell {...props} to={`/boards/${params.investorId}/${widget.linkContentId}/${props.dataItem.objectId}`} />
      ) : (
        <TextCell {...props} />
      );
    };

    const StaticPropertyRender = () => {
      return (props) => <StaticPropertyCell {...props} />;
    };

    const CashflowCellRender = (column) => {
      return (props) => <CashflowCell {...props} columnName={column.name} selectedCurrency={selectedCurrency} boardId={params.boardId || boardData.boardId}
                                      handleCashflowOpen={openCashflowDialog} singlePeriod={setup?.singlePeriod}
                                      scale={column.scale} investorObjectId={params.investorId || widget.investorObjectId} />;
    };

    const FooterCellRender = (gridData: IGridPreparedData[], name: string, totalCount: boolean, scale: number) => {
      if (totalCount) return (props) => <GridFooterCell {...props} gridData={resultState.data} name={name}
                                                        includeTotalCount={totalCount} scale={scale}/>;
    };

    const NumericCellRender = (scale) => {
      return (props) => <NumericCell {...props} scale={scale} />;
    };

    const hasDifferentCurrencies = (data: any[], focusedCurrency?: ICurrency) => {
      if (!focusedCurrency || data.length == 0) return false;
      if (focusedCurrency.currency !== '_') return false;

      const currency = data[0].currency;
      return !!data.find((item) => item.currency !== currency);
    };

    useEffect(() => {
      //@ts-ignore
      setResultState(process(gridData, initialDataState));
      setDataState(initialDataState);
      setMultipleCurrencies(hasDifferentCurrencies(gridData, selectedCurrency));
    }, [gridData]);

    useEffect(() => {
      filterCtx.setGridWidgetFilterState(dataState);
    }, [dataState]);

    return (
      <>
        {isMobile && <ul className='mobile-grid-wrapper'>
          {resultState.data?.map((dataItem, key) => {
            return <MobileDataItem dataItem={dataItem} key={key} boardId={params.boardId || boardData.boardId}
                                   selectedCurrency={selectedCurrency}
                                   investorObjectId={params.investorId || widget.investorObjectId}
                                   staticProperties={boardData.staticProperties} columns={gridColumns}
                                   handleOpenCashflow={openCashflowDialog} singlePeriod={setup.singlePeriod}
                                   link={`/boards/${params.investorId}/${widget.linkContentId}/${dataItem.objectId}`}
                                   isLink={widget.linkContentId && boards.find((item) => item.id === widget.linkContentId)} />;
          })}
        </ul>}
        {!isMobile && <ExcelExport data={resultState.data} ref={ref} fileName={widget.name || t('EXPORT')}>
          <Grid
            data={resultState.data}
            {...dataState}
            resizable={true}
            onDataStateChange={onDataStateChange}
            sortable={true}
            className={`numeric-table-style-wrapper ${gridClassName ? gridClassName : ''}`}
            scrollable='scrollable'>
            <GridColumn field='name' title={t('WIDGET.GRID_TITLE')}
                        width={300} filter='text' columnMenu={ColumnMenu} locked cell={LinkCellRender}
                        footerCell={checkIFThereIsTotal(gridColumns) ? (props) => (
                          <td {...props} style={props.style}
                              className='total-footer-cell'>{t('WIDGET.TOTAL')}</td>) : undefined}
            />
            {selectedCurrency?.currency === '_' && <GridColumn field='currency' title={t('WIDGET.GRID_CURRENCY')} width={120} />}
            {gridColumns.map((column, index) => {
              if (column.type === 'property') {
                return (
                  <GridColumn
                    field={getFieldName(column, false)}
                    title={column.name}
                    width={227}
                    key={index}
                    headerClassName={'table-header-center-wrap'}
                    //headerCell={HeaderCellRender(column, column.hasNotes || false, column.notes || [])}
                    cell={StaticPropertyRender()}
                  />
                );
              }
              return (
                <GridColumn
                  field={getFieldName(column, false)}
                  title={column.name}
                  width={205}
                  key={index}
                  sortable={true}
                  cell={column.viewCashflows ? CashflowCellRender(column) : NumericCellRender(column.scale)}
                  headerClassName={'table-header-center-wrap'}
                  //headerCell={HeaderCellRender(column, column.hasNotes || false, column.notes || [])}
                  footerCell={column.total ? FooterCellRender(gridData, column.name || '', column.total && !multipleCurrencies, column.scale) : undefined}
                />
              );
            })}
          </Grid>
        </ExcelExport>}
      </>
    );
  },
);

export default SinglePeriodGrid;
